<template>
  <a-row :gutter="16">
    <a-col :span="12">
      <a-form-item
        label="视图名称"
        name="name"
        :rules="rules.name"
      >
        <a-input
          v-model:value="model.name"
          placeholder="请填写视图名称"
          allow-clear
        />
      </a-form-item>
    </a-col>
    <a-col :span="12">
      <a-form-item
        label="视图标识"
        name="flag"
        :rules="rules.flag"
        extra="全库唯一；仅英文大小写字母和数字（禁下划线/横杠），用于 URL 与菜单"
      >
        <a-input
          v-model:value="model.flag"
          placeholder="如 salesOrderList"
          allow-clear
        >
          <template
            v-if="form_type === 'create'"
            #suffix
          >
            <a-button
              type="link"
              size="small"
              style="padding: 0"
              :loading="generatingFlag"
              @click="genFlag"
            >
              换一个
            </a-button>
          </template>
        </a-input>
      </a-form-item>
    </a-col>
    <a-col :span="12">
      <a-form-item
        label="数据源"
        name="data_source_id"
        :rules="rules.data_source_id"
      >
        <a-select
          v-model:value="model.data_source_id"
          placeholder="请选择数据源"
          show-search
          option-filter-prop="label"
          :options="dataSourceOptions"
          :loading="dsLoading"
        />
      </a-form-item>
    </a-col>
    <a-col :span="24">
      <a-form-item
        label="查询SQL"
        name="sql"
        :rules="rules.sql"
        extra="只读查询；可用模板变量 {{org_id}}、{{person_id}}（jinja2 渲染）"
      >
        <a-textarea
          v-model:value="model.sql"
          :rows="8"
          placeholder="SELECT id, name FROM t_order WHERE org_id = {{org_id}}"
        />
      </a-form-item>
      <div class="sql-toolbar">
        <!-- ★ v29.23（用户定案）：「探测列」按钮移除——列探测统一收敛到列配置步骤的
             「列探测刷新」（基于此处 SQL+数据源实时探测并合并配置） -->
        <a-button
          size="small"
          :disabled="!model.data_source_id || !model.sql"
          :loading="previewing"
          @click="runPreview"
        >
          预览（前 100 行）
        </a-button>
        <span class="meta-tip">先选数据源并填写 SQL；预览走配置期接口，不落库</span>
      </div>
      <a-alert
        v-if="sqlError"
        type="error"
        show-icon
        :message="sqlError"
        class="sql-alert"
      />
      <div
        v-if="previewRows.length"
        class="preview-block"
      >
        <div class="meta-tip">预览结果：共 {{ previewTotal }} 行（最多回传 100 行）</div>
        <a-table
          :columns="previewCols"
          :data-source="previewRows"
          row-key="__i"
          size="small"
          :pagination="false"
          :scroll="{ x: 640, y: 220 }"
        />
      </div>
    </a-col>
  </a-row>
</template>

<script setup lang="ts">
import { message } from 'ant-design-vue';
import { computed, onMounted, ref, watch } from 'vue';
import { getApiData } from 'liyu-pc-base/api/api-model';
import { useUserStore } from 'liyu-pc-base/init-plugin';
import { extractData, toList } from './helpers';

/* ★ v29.35（用户定案）：`dbViewConfig.vue` 按步骤拆分——「基础配置」步（Step 1）。
   本步持有：视图名称 / 视图标识(flag，含「换一个」) / 数据源 / 查询SQL + 预览结果。

   两条拆分约定（四个步骤组件一致）：
   ① `model` 仍由外壳持有并原样传进来（**对象引用共享**：外壳 `model.value` 与这里
      `props.model` 是同一个对象）→ 模板里照旧写 `model.xxx`，v-model 直接改属性即可。
      外壳会整个换掉对象（编辑态 `initForEdit` 用 `model.value = {...}` 重建），
      故此处用 `computed(() => props.model)` 取用，才能跟着换到新对象（持有裸引用会失联）。
      eslint 的 `vue/no-mutating-props` 不会误报（模板写的是 `model.xxx`，不是 `props.model.xxx`）。
   ② 校验用的 `rules` 只服务本步这四个字段 → 就近定义在这里；`a-form` 仍是外壳的，
      本组件的 `a-form-item` 在它内部 —— provide/inject 走组件树，外壳 `validateFields` 照样找得到。 */

interface Props {
  model: any;
  record: any;
  formType: string;
  /** 预览错误由外壳持有：第 3 步「列探测刷新」开始前也要清它（StepColumnConfig 会 emit probeStart） */
  sqlError: string;
}
const props = defineProps<Props>();
const emit = defineEmits(['update:sqlError']);

const model = computed(() => props.model);
const form_type = computed(() => props.formType);

/* 预览错误：写成「可写 computed」代理到外壳，本组件内部照旧 `sqlError.value = ''` 赋值，
   不必在每处写 emit（原实现的读写点一个没动）。 */
const sqlError = computed<string>({
  get: () => props.sqlError,
  set: (v: string) => emit('update:sqlError', v),
});

const userStore = useUserStore();
const current_org = userStore.currentOrg;

const rules: any = {
  name: [{ required: true, message: '请填写视图名称' }],
  flag: [
    { required: true, message: '请填写视图标识' },
    { pattern: /^[A-Za-z0-9]+$/, message: '仅英文大小写字母和数字，禁下划线/横杠' },
  ],
  data_source_id: [{ required: true, message: '请选择数据源' }],
  sql: [{ required: true, message: '请填写查询SQL' }],
};

/* ═══ 下拉数据源 ═══ */
const dataSourceOptions = ref<any[]>([]);
const dsLoading = ref(false);

const loadDataSources = async () => {
  dsLoading.value = true;
  try {
    const res = await getApiData(
      { org_id: current_org?.id, is_pagination: false },
      `${props.record.model_app}/query_db_data_source_list`,
    );
    dataSourceOptions.value = toList(res).map((it: any) => ({
      value: it.id,
      label: it.name,
    }));
  } catch (e) {
    dataSourceOptions.value = [];
  } finally {
    dsLoading.value = false;
  }
};

/* ★ v29.12（用户定案）：新建视图时自动调 gen_view_flag 生成唯一默认 flag（用户仍可手改/换一个） */
const generatingFlag = ref(false);
const genFlag = async () => {
  generatingFlag.value = true;
  try {
    const res: any = await getApiData(
      { org_id: current_org?.id },
      `${props.record.model_app}/gen_view_flag`,
    );
    const payload = extractData(res);
    if (payload?.flag) {
      model.value.flag = payload.flag;
    } else {
      message.warning((res && res.message) || '生成视图标识失败，请手动填写');
    }
  } catch (e: any) {
    message.warning(e?.message || '生成视图标识失败，请手动填写');
  } finally {
    generatingFlag.value = false;
  }
};

/* ═══ SQL 配置期验证：预览（前 100 行）═══
   ★ v29.23：probeCols / runProbe（探测列按钮）移除——探测统一收敛到列配置步骤的
   列探测刷新（runProbeRefresh）。
   ★ v29.23 预览恒空修复：后端响应键 `data` → `rows`——宿主 request 层直接回吐
   result 负载，extractData 以 res.data 判信封，负载键恰叫 data 时行数组被误当
   信封二次解包 → payload 变数组 → 恒「结果为空」。 */
const previewing = ref(false);
const previewRows = ref<any[]>([]);
const previewCols = ref<any[]>([]);
const previewTotal = ref(0);

/* SQL / 数据源变化后旧结果即失效，直接清掉避免误导 */
watch(
  () => [model.value.sql, model.value.data_source_id],
  () => {
    sqlError.value = '';
    previewRows.value = [];
    previewCols.value = [];
    previewTotal.value = 0;
  },
);

const runPreview = async () => {
  if (!model.value.data_source_id || !model.value.sql) return;
  previewing.value = true;
  sqlError.value = '';
  try {
    const res: any = await getApiData(
      { org_id: current_org?.id, data_source_id: model.value.data_source_id, sql: model.value.sql },
      `${props.record.model_app}/preview_sql`,
    );
    const payload = extractData(res);
    const rows: any[] = Array.isArray(payload?.rows) ? payload.rows : [];
    if (res && res.success === false) {
      sqlError.value = res.message || 'SQL 执行失败';
      previewRows.value = [];
    } else if (!rows.length) {
      sqlError.value = 'SQL 执行成功，但结果为空（0 行）';
      previewRows.value = [];
      previewTotal.value = 0;
    } else {
      previewRows.value = rows.map((r: any, i: number) => ({ ...r, __i: i }));
      previewCols.value = Object.keys(rows[0] || {}).map(k => ({
        title: k,
        dataIndex: k,
        key: k,
        width: 160,
        ellipsis: true,
      }));
      previewTotal.value = payload?.total ?? rows.length;
    }
  } catch (e: any) {
    sqlError.value = e?.message || 'SQL 预览失败';
    previewRows.value = [];
  } finally {
    previewing.value = false;
  }
};

onMounted(() => {
  loadDataSources();
  /* ★ v29.12：新建流程自动生成唯一默认 flag（原先在外壳 onMounted 的 create 分支里，
     现随本步骤下沉；外壳的 formType 在 setup 顶层就已初始化，此处读到的一定是正确值） */
  if (props.formType !== 'edit') genFlag();
});
</script>

<style scoped lang="less">
.meta-tip {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  line-height: 1.5;
}
.sql-toolbar {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
  .meta-tip {
    margin-top: 0;
  }
}
.sql-alert {
  margin-bottom: 8px;
}
.preview-block {
  margin-bottom: 8px;
}
</style>
